import { useGetPermissions } from 'api-hooks/auth/query';
import { Search } from 'baseui/icon';
import { Spinner } from 'baseui/spinner';
import { colors } from 'baseui/tokens';
import { OverlayContainer } from 'common/layouts/styles';
import { themedStyled } from 'common/styles/styletron.theme';
import ErrorViewComponent from 'components/elements/error-view-component';
import FetchWrapperComponent from 'components/elements/fetch-wrapper-component';
import DefaultInput from 'components/elements/master/default-input';
import useDisclosure from 'hooks/use-disclosure';
import useSearchMenu from 'hooks/use-search-menu';
import * as React from 'react';

import NavigationComponent from './navigation-component';
import {
  SideNavigationContainer,
  NavigationContainer,
  LoadingContainer,
} from '../styles';

export function NavStyle(props: any) {
  const { $active, $theme, $level, $selectable } = props;
  if (!$active) {
    if ($selectable) {
      return {
        borderLeftColor: '#787878',
        borderWidth: '2px',
        color: '#787878',
        ':hover': {
          color: '#707176',
          fontWeight: '400',
        },
      };
    } else {
      if ($level > 1) {
        return {
          color: 'white',
          fontWeight: '500',
        };
      } else {
        return {
          color: 'white',
          fontWeight: '500',
        };
      }
    }
  }

  return {
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    borderLeftColor: $theme.colors.secondary1,
    borderWidth: '4px',
    color: 'white',
    ':hover': {
      color: 'white',
    },
  };
}

export type SideNavigationRef = {
  isNavOpen: boolean;
  close: () => void;
  open: () => void;
  toggle: () => void;
};

const SideNavigation = React.forwardRef<SideNavigationRef>((props, ref) => {
  const [isNavOpen, { close, open, toggle }] = useDisclosure(true);
  const {
    FILTERED_NAVIGATION,
    searchInputRef,
    searchNavigation,
    setSearchNavigation,
    onFocusSearch,
  } = useSearchMenu();

  const queryPermissions = useGetPermissions();

  React.useImperativeHandle(ref, () => {
    return {
      isNavOpen,
      close,
      open,
      toggle,
    };
  });

  React.useEffect(() => {
    document.addEventListener('keydown', onFocusSearch, false);

    return () => {
      document.removeEventListener('keydown', onFocusSearch, false);
    };
  }, [onFocusSearch]);

  return (
    <>
      <SideNavigationContainer
        $isActive={isNavOpen}
        className="side-navigation-container"
      >
        <FetchWrapperComponent
          errorVertical
          isLoading={queryPermissions.isLoading}
          error={!!queryPermissions.error}
          loadingComponent={
            <LoadingContainer>
              <Spinner $color="white" />
            </LoadingContainer>
          }
          errorComponent={
            <LoadingContainer>
              <ErrorViewComponent vertical refetch={queryPermissions.refetch} />
            </LoadingContainer>
          }
          component={
            <NavigationContainer $isActive={isNavOpen}>
              <div
                style={{
                  position: 'sticky',
                  padding: 16,
                  top: 0,
                  zIndex: 3,
                  backgroundColor: 'white',
                }}
              >
                <DefaultInput
                  ref={searchInputRef}
                  placeholder="Search: Ctrl / ⌘ + Shift + F"
                  endEnhancer={
                    <SearchIconContainer>
                      <Search
                        size={20}
                        color={searchNavigation ? colors.black : colors.gray600}
                      />
                    </SearchIconContainer>
                  }
                  onChange={(e) => setSearchNavigation(e?.target?.value || '')}
                />
              </div>

              {FILTERED_NAVIGATION.map((nav, idx) => (
                <NavigationComponent
                  key={`${nav.title}${searchNavigation ? '-inSearch' : ''}`}
                  route={nav}
                  depth={1}
                  isOpen={searchNavigation !== ''}
                />
              ))}
            </NavigationContainer>
          }
        />
      </SideNavigationContainer>
      <OverlayContainer onClick={close} $isActive={isNavOpen} />
    </>
  );
});

export default SideNavigation;

const SearchIconContainer = themedStyled('div', {
  display: 'flex',
  alignItems: 'center',
  paddingRight: '8px',
});
